import * as React from 'react';
import { observer } from 'mobx-react';
import InputWrapper, { InputType } from '../Inputs/InputWrapper';
import { observable, makeObservable, runInAction } from 'mobx';
import { DisplayType } from '../Models/Enums';
import * as uuid from 'uuid';
import InputsHelper from '../Helpers/InputsHelper';
import classNames from 'classnames';
import { BoundProp, getBoundValue, setBoundValue } from '../Inputs/BoundModelProperty';

export interface IPasswordProps<T> {
	model: T;
	modelProperty: BoundProp<T, string>;
	id?: string;
	name?: string;
	className?: string;
	displayType?: DisplayType;
	label?: React.ReactNode;
	labelVisible?: boolean;
	isRequired?: boolean;
	isDisabled?: boolean;
	isReadOnly?: boolean;
	disablePreview?: boolean;
	tooltip?: string;
	subDescription?: string;
	placeholder?: string;
	clickToClear?: boolean;
	errors?: string | string[];
	onAfterChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
	onChangeAndBlur?: (event: React.ChangeEvent<HTMLInputElement>) => void;
	inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
	passwordPreviewIcon?: string;
	inputRef?: React.LegacyRef<HTMLInputElement> | undefined;
}

class Password<T> extends React.Component<IPasswordProps<T>> {
	public static defaultProps = {
		inputProps: {},
		className: '',
	};

	private uuid = uuid.v4();

	private valueWhenFocused: string = '';

	private showPassword: boolean = false;

	constructor(props: IPasswordProps<T>) {
		super(props);

		makeObservable<Password<T>, 'showPassword'>(this, {
			showPassword: observable,
		});
	}

	private setShowPassword(showPassword: boolean) {
		runInAction(() => {
			this.showPassword = showPassword;
		});
	}

	public render() {
		const {
			model,
			modelProperty,
			name,
			label,
			isDisabled,
			isReadOnly,
			isRequired,
			disablePreview,
			tooltip,
			subDescription,
			placeholder,
			id = this.uuid.toString(),
			className,
			labelVisible,
			inputProps,
			inputRef,
			displayType,
			errors,
			passwordPreviewIcon = 'icon-look',
		} = this.props;

		const fieldId = `${id}-field`;
		const ariaLabel = InputsHelper.getAriaLabel(label, labelVisible);
		const ariaDescribedby = InputsHelper.getAriaDescribedBy(id, tooltip, subDescription);

		return (
			<InputWrapper
				inputType={InputType.PASSWORD}
				id={id || this.uuid.toString()}
				inputId={fieldId}
				className={classNames(className, 'password-container')}
				displayType={displayType}
				isRequired={isRequired}
				tooltip={tooltip}
				subDescription={subDescription}
				label={label}
				labelVisible={labelVisible === undefined ? true : labelVisible}
				errors={errors}
			>
				<input
					type={this.showPassword ? 'text' : 'password'}
					autoCapitalize="off"
					autoCorrect="off"
					name={name}
					id={fieldId}
					value={getBoundValue(model, modelProperty) || ''}
					onChange={this.onChange}
					onBlur={this.onBlur}
					placeholder={placeholder ?? ariaLabel}
					disabled={isDisabled}
					readOnly={isReadOnly}
					ref={inputRef}
					aria-label={ariaLabel}
					aria-describedby={ariaDescribedby}
					{...inputProps}
				/>
				{disablePreview ? null : (
					<span
						role="button"
						className={`icon-only ${
							this.showPassword ? 'icon-look-off' : passwordPreviewIcon
						} password-input-icon icon`}
						onClick={() => this.setShowPassword(!this.showPassword)}
					/>
				)}
			</InputWrapper>
		);
	}

	private onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		runInAction(() => {
			setBoundValue(this.props.model, this.props.modelProperty, event.target.value);
		});
		if (this.props.onAfterChange) {
			this.props.onAfterChange(event);
		}
	};

	private onBlur = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (this.valueWhenFocused !== event.target.value && this.props.onChangeAndBlur) {
			this.props.onChangeAndBlur(event);
		}
	};
}

export default observer(Password);
